import React from 'react';
import { Tooltip } from 'react-tooltip';

import { ReactComponent as DeleteIcon } from '@assets/svg/gps-asset-tracking/ic-delete.svg';
import { ReactComponent as EditIcon } from '@assets/svg/gps-asset-tracking/ic-edit.svg';
import { ReactComponent as DisassociateIcon } from '@assets/svg/gps-asset-tracking/ic-disassociate.svg';
import { ReactComponent as AssociateIcon } from '@assets/svg/gps-asset-tracking/ic-associate.svg';

import { MODAL_NAMES } from '@constants/modalRenderers';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import type { IGPSTracker } from '@model/gpsTracker';

import { useGPSAssetDeleteMutation } from '@pages/GPSAssetTracking/services/useGPSAssetDeleteMutation';
import { useGPSTrackerDeleteMutation } from '@pages/GPSAssetTracking/services/useGPSTrackerDeleteMutation';
import {
  selectActiveAssetInfo,
  useAssetTrackerActions,
} from '@pages/GPSAssetTracking/store/useAssetTrackerStore';

import { useModalActions } from '@store/useModalStore';

import style from './style.module.scss';

interface IProps {
  id: string;
}

const TrackerContextMenu: React.FC<IProps> = ({ id }) => {
  const { t } = useTranslation();

  const { setActiveModal, setModalClose } = useModalActions();
  const { setClearActiveAsset } = useAssetTrackerActions();

  const activeAssetInfo = selectActiveAssetInfo();

  const { mutate: gpsTrackerDeleteMutation } = useGPSTrackerDeleteMutation();
  const { mutate: gpsAssetDeleteMutation } = useGPSAssetDeleteMutation();

  const handleDeleteTracker = (tracker: IGPSTracker) => {
    setActiveModal({
      name: MODAL_NAMES.modalConfirm,
      options: {
        bodyTitle: `${t('Delete GPS Tracker')} "${tracker?.model} serial number: ${tracker?.serialNumber}"`,
        bodyContent: t('Are you sure you want to delete the selected gps tracker?'),
        onConfirm: () => {
          gpsTrackerDeleteMutation(tracker?.id, {
            onSuccess: () => showToast({ variant: 'success', message: t('Your changes have been saved.') }),
          });
          setModalClose();
        },
        confirmText: t('Delete'),
      },
    });
  };

  const handleDeleteAsset = (tracker: IGPSTracker) => {
    setActiveModal({
      name: MODAL_NAMES.modalConfirm,
      options: {
        bodyTitle: `${t('Delete Asset Tracking')} Serial ${tracker.serialNumber}`,
        bodyContent: (
          <>
            {t('Are you sure you want to delete the selected asset tracking?')}
            <div className={style.extraText}>{t('Asset is associated with the GPS tracker')}</div>
            <div className={style.bodyContentField}>{tracker.fullName}</div>
          </>
        ),
        onConfirm: () => {
          gpsAssetDeleteMutation(tracker.asset.id, {
            onSuccess: () => {
              showToast({ variant: 'success', message: t('Your changes have been saved.') });

              if (activeAssetInfo?.id === tracker.asset?.id) {
                setClearActiveAsset();
              }
              setModalClose();
            },
          });
          setModalClose();
        },
        confirmText: t('Delete'),
      },
    });
  };

  return (
    <Tooltip
      id={id}
      place="left"
      openOnClick
      className={style.contextMenuPopover}
      clickable
      disableStyleInjection
      noArrow
      render={({ content }) => {
        if (!content) {
          return null;
        }
        const row: IGPSTracker = JSON.parse(content);

        return (
          <>
            <div
              className={style.contextMenuPopoverItem}
              onClick={() =>
                setActiveModal({ name: MODAL_NAMES.createEditGpsTrackerModal, options: { gpsTracker: row } })
              }
            >
              <EditIcon className={style.menuIcon} />
              {t('Edit Tracker')}
            </div>

            {!row.asset?.assetTag && (
              <div
                className={style.contextMenuPopoverItem}
                onClick={() =>
                  setActiveModal({
                    name: MODAL_NAMES.createEditAssetTrackingModal,
                    options: { gpsTracker: row },
                  })
                }
              >
                <AssociateIcon className={style.menuIcon} />
                {t('Associate asset')}
              </div>
            )}
            {row.asset?.assetTag && (
              <div className={style.contextMenuPopoverItem} onClick={() => handleDeleteAsset(row)}>
                <DisassociateIcon className={style.menuIcon} />
                {t('Disassociate asset')}
              </div>
            )}
            <div className={style.contextMenuPopoverItem} onClick={() => handleDeleteTracker(row)}>
              <DeleteIcon className={style.menuIcon} />
              {t('Delete tracker')}
            </div>
          </>
        );
      }}
    />
  );
};

export default TrackerContextMenu;
